import styles from "@/asserts/styles/formSection.module.less";
import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import { DownOutlined } from "@ant-design/icons";
import { DeptSelectorModal } from "@/pages/supplier/AssessmentTemplate/components/DeptSelectorModal";
import type { DeptItem } from "@/pages/supplier/AssessmentTemplate/components/DeptSelectorModal/type";
import { useModalBehavior } from "@/hooks/behaviors/useModalBehavior";
import { Col, DatePicker, Form, Input, Radio, Row, Select } from "antd";
import type { HighValuePurchaseRequisitionFormValue } from "../../type";
import { CascaderFields } from "../CascaderFields";
import {
  useCostCenterOptions,
  useGLAccountOptions,
  useMaterialGroupOptions,
} from "@/hooks/services/useSelectOptionsQuery";
import { useFormEditable } from "../../hooks/useFormEditable";
import { purchaseGroupOptions } from "@/constants";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;

export const ApplicationInfoFormFields = () => {
  const { t } = useTranslation("purchaseRequestManagement");
  const { t: tCommon } = useTranslation("common");
  const { open, closeModal, openModal } = useModalBehavior();
  const form = Form.useFormInstance<HighValuePurchaseRequisitionFormValue>();

  const factoryValue = Form.useWatch("factoryValue", form);
  const costCenterOptions = useCostCenterOptions(factoryValue!);
  const materialGroupOptions = useMaterialGroupOptions();
  const glAccountOptions = useGLAccountOptions(factoryValue!);

  const handleDeptSelect = (dept: DeptItem) => {
    form.setFieldValue("expenseDeptNo", dept.deptNo);
    form.setFieldValue("expenseDeptName", dept.deptName);
    closeModal();
  };

  const {
    isPurchasingStaffFormFieldsDisabled,
    isFinancialFirstAuditFormFieldsDisabled,
  } = useFormEditable();

  return (
    <>
      <div className={styles.formSection}>
        <ModalSectionHeader title={t("申请信息")} />

        <div className={styles.sectionBody}>
          <Row gutter={49}>
            <CascaderFields />

            <Col span={8}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="expenseDeptName"
                label={t("费用归属部门")}
                rules={[
                  {
                    required: true,
                    message: t("请选择费用归属部门"),
                  },
                ]}
              >
                <Input
                  placeholder={t("请选择")}
                  readOnly
                  suffix={<DownOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  disabled={isFinancialFirstAuditFormFieldsDisabled}
                  onClick={openModal}
                />
              </Form.Item>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="expenseDeptNo"
                hidden
              >
                <Input placeholder={t("请选择")} readOnly />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={49}>
            {/* 成本中心 必填 */}
            <Col span={8}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="costCenterNo"
                label={t("成本中心")}
                rules={[
                  {
                    required: true,
                    message: t("请选择成本中心"),
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("请选择")}
                  options={costCenterOptions}
                  disabled={isFinancialFirstAuditFormFieldsDisabled}
                />
              </Form.Item>
            </Col>

            {/* 会计科目 必填 */}
            <Col span={8}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="glAccount"
                label={t("会计科目")}
                rules={[
                  {
                    required: true,
                    message: t("请选择会计科目"),
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("请选择")}
                  options={glAccountOptions}
                  disabled={isFinancialFirstAuditFormFieldsDisabled}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="materialGroup"
                label={t("物料组")}
                rules={[
                  {
                    required: true,
                    message: t("请选择物料组"),
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("请选择")}
                  options={materialGroupOptions}
                  disabled={isPurchasingStaffFormFieldsDisabled}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="purchaseGroup"
                label={t("采购组")}
                rules={[
                  {
                    required: true,
                    message: t("请选择采购组"),
                  },
                ]}
              >
                <Select
                  placeholder={t("请选择")}
                  options={purchaseGroupOptions}
                  disabled={isPurchasingStaffFormFieldsDisabled}
                />
              </Form.Item>
            </Col>

            {/* 期望到货日期 非必填 */}
            <Col span={8}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="deliveryDate"
                label={t("期望到货日期")}
              >
                <DatePicker
                  placeholder={t("请选择")}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            {/* 是否紧急采购 必填 */}
            <Col span={8}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="isUrgent"
                label={t("是否紧急采购")}
                rules={[
                  {
                    required: true,
                    message: t("请选择是否紧急采购"),
                  },
                ]}
              >
                <Select placeholder={t("请选择")}>
                  <Select.Option value={0}>{tCommon("否")}</Select.Option>
                  <Select.Option value={1}>{tCommon("是")}</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            {/* 供应商类型 必填 */}
            <Col span={8}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="supplierType"
                label={t("供应商")}
                rules={[
                  {
                    required: true,
                    message: t("请选择供应商"),
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value={1}>{t("单一")}</Radio>
                  <Radio value={2}>{t("多个")}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={49}>
            {/* 指定供应商及型号 非必填 */}
            <Col span={12}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="suggestedSupplierName"
                label={t("指定供应商及型号")}
              >
                <TextArea rows={2} placeholder={t("请输入")} />
              </Form.Item>
            </Col>

            {/* 指定原因 非必填 */}
            <Col span={12}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="suggestedReason"
                label={t("指定原因")}
              >
                <TextArea rows={2} placeholder={t("请输入")} />
              </Form.Item>
            </Col>

            {/* 采购物品描述 必填 */}
            <Col span={12}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="purchaseDescription"
                label={t("采购物品描述")}
                rules={[
                  {
                    required: true,
                    message: t("请输入采购物品描述"),
                  },
                ]}
              >
                <TextArea rows={2} placeholder={t("请输入")} />
              </Form.Item>
            </Col>

            {/* 采购目的 必填 */}
            <Col span={12}>
              <Form.Item<HighValuePurchaseRequisitionFormValue>
                name="purchasePurpose"
                label={t("采购目的")}
                rules={[
                  {
                    required: true,
                    message: t("请输入采购目的"),
                  },
                ]}
              >
                <TextArea rows={2} placeholder={t("请输入")} />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </div>
      <DeptSelectorModal
        open={open}
        onClose={closeModal}
        onOk={handleDeptSelect}
      />
    </>
  );
};
